import { useEffect, useRef, useState } from "react";

/**
 * SearchableSelect — Combobox ricercabile con dropdown VISIBILE.
 * Sostituisce <datalist> nativo HTML5 con un'esperienza chiara, responsive
 * (PC + tablet + smartphone + app Capacitor) e brand-consistent.
 *
 * Props:
 *   - value           : valore selezionato corrente (string)
 *   - onChange(v)     : callback quando si seleziona / digita
 *   - options         : array di opzioni (string[] o {label,value,extra}[])
 *   - placeholder     : testo placeholder input
 *   - allowFreeText   : se true, ogni keystroke chiama onChange (utile per modelli auto custom)
 *   - getLogo(opt)    : funzione che ritorna URL logo per l'opzione (opzionale)
 *   - testid          : prefisso data-testid per testing
 *   - inputClassName  : override classi input
 *   - maxResults      : limite risultati mostrati (default 200)
 *   - emptyHint       : testo quando nessun risultato
 */
export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = "Cerca…",
  allowFreeText = false,
  getLogo = null,
  testid = "searchable",
  inputClassName,
  maxResults = 200,
  emptyHint,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Mantieni query in sync con value esterno (solo quando NON è aperto, per non disturbare la digitazione)
  useEffect(() => {
    if (!open) setQuery(value || "");
  }, [value, open]);

  // Chiudi al click fuori
  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    document.addEventListener("touchstart", h, { passive: true });
    return () => {
      document.removeEventListener("mousedown", h);
      document.removeEventListener("touchstart", h);
    };
  }, []);

  // Normalize options: sempre {label, value, extra?}
  const norm = options.map((o) => (typeof o === "string" ? { label: o, value: o } : o));

  const q = (query || "").toLowerCase().trim();
  const filtered = q
    ? norm.filter((o) =>
        (o.label || "").toLowerCase().includes(q) ||
        (o.extra || "").toLowerCase().includes(q)
      )
    : norm;

  const defaultInputCls =
    "mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-[#ff0033]/60 focus:outline-none placeholder:text-white/35";

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (allowFreeText) onChange(e.target.value);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            const hit = filtered[0];
            if (hit) { onChange(hit.value); setQuery(hit.label); setOpen(false); }
            else if (allowFreeText) { onChange(query); setOpen(false); }
          } else if (e.key === "Escape") {
            setOpen(false);
            setQuery(value || "");
          }
        }}
        placeholder={placeholder}
        className={inputClassName || defaultInputCls}
        autoComplete="off"
        data-testid={`${testid}-input`}
      />
      {open && (
        <div
          className="absolute z-50 left-0 right-0 top-full mt-1 max-h-72 overflow-auto rounded-xl border border-white/15 bg-[#0a0a0a]/98 backdrop-blur-md shadow-2xl shadow-black/60"
          data-testid={`${testid}-dropdown`}
          role="listbox"
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-xs text-white/40">
              {emptyHint || (
                <>Nessun risultato.{allowFreeText && query && <> Premi <kbd className="px-1 py-0.5 bg-white/[0.05] rounded text-[10px] mx-1">Invio</kbd> per usare "<b className="text-white/70">{query}</b>".</>}</>
              )}
            </div>
          ) : (
            <>
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5 sticky top-0 bg-[#0a0a0a]">
                {filtered.length} risultat{filtered.length === 1 ? "o" : "i"}
                {q ? <> per "<span className="text-white/70">{query}</span>"</> : <> · scorri o digita per filtrare</>}
              </div>
              {filtered.slice(0, maxResults).map((opt) => {
                const logo = getLogo ? getLogo(opt.value) : null;
                const active = (value || "").toLowerCase() === (opt.value || "").toLowerCase();
                return (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`${testid}-option-${opt.value}`}
                    onClick={() => { onChange(opt.value); setQuery(opt.label); setOpen(false); inputRef.current?.blur(); }}
                    className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 border-b border-white/[0.03] last:border-0 hover:bg-[#ff0033]/15 active:bg-[#ff0033]/25 transition ${active ? "bg-[#ff0033]/20 text-[#ff5577]" : "text-white/85"}`}
                  >
                    {logo && (
                      <img src={logo} alt="" className="h-5 w-5 bg-white rounded p-0.5 object-contain flex-shrink-0"
                        onError={(e) => { e.target.style.display = "none"; }} />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{opt.label}</div>
                      {opt.extra && <div className="text-[10px] text-white/40 truncate">{opt.extra}</div>}
                    </div>
                    {active && <span className="text-[#ff5577] flex-shrink-0">✓</span>}
                  </button>
                );
              })}
              {filtered.length > maxResults && (
                <div className="px-3 py-2 text-[10px] text-white/35 italic">
                  +{filtered.length - maxResults} risultati nascosti. Affina la ricerca…
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
